import { isFeatureExpired } from './SelectionDeadlineUtility';
import IsNullOrUndefined from "../utils/ObjectUtils";
import {CategoryType} from "../model/constants";

export const RulesUtility = {

    // Finds, for a given rule, the sibling categories whose selected_feature doesn't
    // match the linked feature that rule assigns to them (skipping categoryIdToSkip).
    // Returns a list of { category, linkedFeature } describing the needed changes,
    // without mutating anything.
    realignLinkedCategories: (rule, categories, categoryIdToSkip) => {
        const changes = [];

        rule.linkedFeatures.forEach(linkedFeature => {
            // Find the category for the linked feature
            const category = categories.find(cat =>
                cat.features.some(feature => feature.id === linkedFeature)
            );

            if (!category) return; // No matching category

            // If this is the category to skip, skip it
            if (category.id === categoryIdToSkip) return;

            // If this category has already been recorded, skip it
            if (changes.some(change => change.category.id === category.id)) return;

            // Check if the category has a conflicting selected feature
            if (category.selected_feature !== 0 && category.selected_feature !== linkedFeature) {
                changes.push({ category, linkedFeature });
            }
        });

        return changes;
    },

    findValidFeatureForCategory: (rules, categories, changedCategory) => {
        if (changedCategory.look_type === CategoryType.Improvement) {
            changedCategory.selected_feature = 0;
        } else {
            const validFeature = changedCategory.features.find(changedFeature => {
                if (isFeatureExpired(changedFeature)) return false;
                // Find all applicable rules for this feature
                const applicableRules = rules.filter(rule => rule.linkedFeatures.includes(changedFeature.id));

                if (applicableRules.length === 0) {
                    return true; // No rules apply, so this feature is always valid
                }

                // Check if this feature can satisfy at least one applicable rule
                return applicableRules.some(rule => {
                    const rulePairsByCategory = rule.linkedFeatures.reduce((acc, featureId) => {
                        const category = categories.find(cat => cat.features.some(f => f.id === featureId));
                        if (category) {
                            if (!acc[category.id]) {
                                acc[category.id] = new Set();
                            }
                            acc[category.id].add(featureId);
                        }
                        return acc;
                    }, {});

                    // Validate rule: each category in the rule must satisfy one of the linked features
                    return Object.keys(rulePairsByCategory).every(categoryId => {
                        if (parseInt(categoryId) === changedCategory.id) return true; // Skip the changed category

                        const selectedFeature = categories.find(cat => cat.id === parseInt(categoryId))?.selected_feature;
                        if (!selectedFeature) return true; // If no feature is selected, it doesn't block

                        // The selected feature must match one of the rule's linked features for that category
                        return rulePairsByCategory[categoryId].has(selectedFeature);
                    });
                });
            });

            // Set the valid feature if found, otherwise try to realign the linked siblings
            // to the category's current selection, only clearing it as a last resort.
            if (validFeature) {
                changedCategory.selected_feature = validFeature.id;
            } else {
                const applicableRules = rules.filter(rule => rule.linkedFeatures.includes(changedCategory.selected_feature));
                const ruleToApply = applicableRules[0];
                const changes = ruleToApply
                    ? RulesUtility.realignLinkedCategories(ruleToApply, categories, changedCategory.id)
                    : [];

                if (ruleToApply && changes.length > 0) {
                    changes.forEach(({ category, linkedFeature }) => {
                        category.selected_feature = linkedFeature;
                    });
                } else {
                    changedCategory.selected_feature = 0; // Clear selection if no valid feature/realignment is found
                }
            }
        }
    },

    validateAllRules: (rules, categories) => {
        categories.forEach(category => {
            if (category.look_type !== CategoryType.Improvement && category.selected_feature) {
                // Find all applicable rules that include the selected feature
                const applicableRules = rules.filter(rule => rule.linkedFeatures.includes(category.selected_feature));

                // If no rules apply to this feature, it's valid by default (no constraints apply)
                if (applicableRules.length === 0) {
                    return; // Skip validation - feature is valid
                }

                // Check if the selected feature is valid according to the applicable rules
                const isValid = applicableRules.some(rule => {
                    // Validate the rule for all linked features
                    return rule.linkedFeatures.every(linkedFeature => {
                        const linkedCategory = categories.find(cat =>
                            cat.features.some(f => f.id === linkedFeature)
                        );

                        if (!linkedCategory) return true; // If the linked category doesn't exist, skip

                        if (linkedCategory.id === category.id) return true; // Skip the current category

                        // If the linked category has a selected feature, it must match the rule
                        return linkedCategory.selected_feature === 0 ||
                            linkedCategory.selected_feature === linkedFeature;
                    });
                });

                // If the selected feature is not valid, find a valid one
                if (!isValid) {
                    RulesUtility.findValidFeatureForCategory(rules, categories, category);
                }
            }
        });
    },

    validateRulesForSelectedFeature: (rules, categories, addedCategoriesFromRule, removedCategoriesFromRule, changedCategory, changedFeature) => {
        // Find all applicable rules that include the changed feature
        const applicableRules = rules.filter(rule => rule.linkedFeatures.includes(changedFeature));

        // If there are no applicable rules, it is automatically valid
        if (applicableRules.length === 0) return true;

        // A rule is satisfied when every other category it links to either has no selection yet
        // or already has the linked feature selected.
        const isRuleSatisfied = (rule) => rule.linkedFeatures.every(linkedFeature => {
            const category = categories.find(cat =>
                cat.features.some(feature => feature.id === linkedFeature)
            );

            if (!category) return true; // No matching category
            if (category.id === changedCategory) return true; // Skip the changed category

            return category.selected_feature === 0 || category.selected_feature === linkedFeature;
        });

        // Satisfying a single applicable rule is enough: if any of them is already satisfied,
        // the current selection is valid and no other category needs to change.
        if (applicableRules.some(isRuleSatisfied)) {
            return true;
        }

        // None of the applicable rules is satisfied as-is: propose the changes needed to
        // satisfy one of them (the first applicable rule).
        const ruleToApply = applicableRules[0];
        const changes = RulesUtility.realignLinkedCategories(ruleToApply, categories, changedCategory);

        changes.forEach(({ category, linkedFeature }) => {
            removedCategoriesFromRule.push({ ...category });
            addedCategoriesFromRule.push({
                ...category,
                selected_feature: linkedFeature
            });
        });

        return false;
    }







    /* findValidFeatureForCategory: (rules, categories, changedCategory) => {
        let foundCompatible = false;
        changedCategory.features.forEach(changedFeature => {
            let isValid = true;
            rules.forEach(rule => {
                if (rule.linkedFeatures.indexOf(changedFeature.id) !== -1)
                {
                    rule.linkedFeatures.forEach(linkedFeature => {
                        categories.forEach(category => {
                            const foundFeature = category.features.find(feature => feature.id === linkedFeature);
                            if (!IsNullOrUndefined(foundFeature) && category.selected_feature !== 0) {
                                if (category.id !== changedCategory.id && category.selected_feature !== linkedFeature) {
                                    isValid = false;
                                }
                            }
                        });
                    });
                }
            });

            if (isValid && !foundCompatible) {
                changedCategory.selected_feature = changedFeature.id;
                foundCompatible = true;

            }
        });
    }, */
};
